import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  XMarkIcon,
  TruckIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
  BriefcaseIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import axios from 'axios';
import { auth } from '../../firebase';

const AddVehicleModal = ({ showAddCarModal, setShowAddCarModal, onCarsChanged, prefilledPlate, carId, isOwnershipVerification }) => {
  const { t } = useTranslation();
  const modalRef = useRef(null);
  // Store original body styles and scroll position
  const bodyStyles = useRef({});

  // Make sure to initialize all fields you might use
  const [newCar, setNewCar] = useState({
    owner: false,
    year: "",
    plate: "",
    color: "",
    vin: "",
    civ: "",
    status: "Activ" // Default status
  });

  // VIN validation state
  const [vinValidated, setVinValidated] = useState(false);
  const [vinValidating, setVinValidating] = useState(false);
  const [vinError, setVinError] = useState(false);

  // CIV validation state
  const [civValidated, setCivValidated] = useState(false);
  const [civValidating, setCivValidating] = useState(false);
  const [civError, setCivError] = useState(false);

  // Car details confirmation state
  const [carDetails, setCarDetails] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  // CIV help modal state
  const [showCivHelpModal, setShowCivHelpModal] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (showAddCarModal) {
      setNewCar({
        owner: prefilledPlate ? true : false, // If prefilled plate, assume taking ownership
        year: "",
        plate: prefilledPlate || "",
        color: "",
        vin: "",
        civ: "",
        status: "Activ"
      });
      setVinValidated(false);
      setVinValidating(false);
      setVinError(false);
      setCivValidated(false);
      setCivValidating(false);
      setCivError(false);
      setCarDetails(null);
      setShowConfirmationModal(false);
      setShowCivHelpModal(false);
    }
  }, [showAddCarModal, prefilledPlate]);

  // Remove the auto-disable owner logic - user can check owner anytime

  // Create a more robust scroll lock mechanism
  useEffect(() => {
    if (showAddCarModal) {
      // Store current scroll position and body styles
      const scrollY = window.scrollY;
      bodyStyles.current = {
        overflow: document.body.style.overflow,
        position: document.body.style.position,
        top: document.body.style.top,
        width: document.body.style.width,
        scrollY
      };

      // Apply scroll lock styles to body
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';

      // Also prevent scroll on html element
      document.documentElement.style.overflow = 'hidden';
    } else if (bodyStyles.current.scrollY !== undefined) {
      // Restore original styles
      document.body.style.overflow = bodyStyles.current.overflow || '';
      document.body.style.position = bodyStyles.current.position || '';
      document.body.style.top = bodyStyles.current.top || '';
      document.body.style.width = bodyStyles.current.width || '';
      document.documentElement.style.overflow = '';

      // Restore scroll position
      window.scrollTo(0, bodyStyles.current.scrollY);
    }

    return () => {
      // Always restore on unmount
      document.body.style.overflow = bodyStyles.current.overflow || '';
      document.body.style.position = bodyStyles.current.position || '';
      document.body.style.top = bodyStyles.current.top || '';
      document.body.style.width = bodyStyles.current.width || '';
      document.documentElement.style.overflow = '';

      if (bodyStyles.current.scrollY !== undefined) {
        window.scrollTo(0, bodyStyles.current.scrollY);
      }
    };
  }, [showAddCarModal]);

  // VIN validation effect
  useEffect(() => {
    // Reset validation state when VIN field is empty
    if (!newCar.vin) {
      setVinValidated(false);
      setVinValidating(false);
      setVinError(false);
      return;
    }

    const checkVinExists = async (vin) => {
      if (vin.length === 17) {
        setVinValidating(true);
        setVinError(false); // Reset error state when starting validation
        try {
          const idToken = await auth.currentUser?.getIdToken();
          const response = await axios.get(
            `https://api.servicegest.ro/driver/car/check-vin-exists?vin=${vin}`,
            {
              headers: {
                'Authorization': `Bearer ${idToken}`,
                'Content-Type': 'application/json',
              }
            }
          );

          // Handle different response scenarios
          // If response.data is empty or undefined, treat as VIN not found (valid for adding)
          // If response.data.exists is explicitly false, VIN not found (valid for adding)
          // If response.data.exists is true, VIN exists (invalid for adding)
          const vinExists = response.data && response.data.exists === true;
          setVinValidated(!vinExists);
          setVinError(vinExists); // Set error state if VIN exists

          console.log('VIN validation response:', response.data, 'vinValidated:', !vinExists);
        } catch (error) {
          // Any API error means we can't verify the VIN - block proceeding
          setVinValidated(false);
          setVinError(true); // Set error state for API failures

          // Log for debugging purposes only
          console.error('API Error in check-vin-exists:', {
            status: error.response?.status,
            message: error.response?.data?.message,
            url: error.config?.url
          });
        } finally {
          setVinValidating(false);
        }
      } else {
        setVinValidated(false);
        setVinValidating(false);
        setVinError(false);
      }
    };

    checkVinExists(newCar.vin);
  }, [newCar.vin]);

  // CIV validation effect
  useEffect(() => {
    const checkCivValid = (civ) => {
      // Pattern: one uppercase letter + six digits (e.g., R012345)
      const civPattern = /^[A-Z]\d{6}$/;
      return civPattern.test(civ);
    };

    if (newCar.civ) {
      setCivValidating(true);
      setCivError(false); // Reset error state when starting validation

      // Simple client-side validation for pattern
      const isValidFormat = checkCivValid(newCar.civ);

      if (isValidFormat) {
        setCivValidated(true);
        setCivError(false);
      } else {
        setCivValidated(false);
        setCivError(true);
      }

      setCivValidating(false);
    } else {
      setCivValidated(false);
      setCivValidating(false);
      setCivError(false);
    }
  }, [newCar.civ]);

  // Remove auto-update owner logic - user can check owner anytime

  // Handle form submission - get car details for confirmation or direct ownership verification
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Registration number is required
    if (!newCar.plate) {
      toast.error(t('vehicleDetails.errors.plateRequired'));
      return;
    }

    // VIN is required only if not ownership verification
    if (!isOwnershipVerification) {
      const hasValidVin = newCar.vin && vinValidated;
      if (!hasValidVin) {
        toast.error(t('vehicleDetails.errors.vinRequired'));
        return;
      }
    }

    // If owner is true, CIV must be valid
    if (newCar.owner) {
      const hasValidCiv = newCar.civ && civValidated;
      if (!hasValidCiv) {
        toast.error(t('firstLogin.civRequiredForOwner') || 'CIV este obligatoriu pentru proprietari');
        return;
      }
    }

    // For ownership verification, skip the check-valid-car step and go directly to verify-ownership
    if (isOwnershipVerification) {
      await handleConfirmAdd();
      return;
    }

    try {
      const idToken = await auth.currentUser?.getIdToken();

      const response = await axios.get(
        `https://api.servicegest.ro/driver/car/check-valid-car`,
        {
          params: {
            owner: newCar.owner,
            vin: newCar.vin,
            civ: newCar.civ || undefined,
            registrationNr: newCar.plate
          },
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          }
        }
      );

      // Store car details and show confirmation modal
      setCarDetails(response.data);
      setShowConfirmationModal(true);
    } catch (error) {
      // Handle different validation errors from the backend
      const errorMessage = error.response?.data;
      console.log(error)

      if (errorMessage === "registration number does not match") {
        toast.error(t('vehicleDetails.errors.vinMismatch'));
      } else if (errorMessage === "CIV's don't match") {
        toast.error(t('vehicleDetails.errors.civMismatch'));
      } else {
        // Generic error message for other cases
        toast.error(t('vehicleDetails.errors.dataNotMatch'));
      }

      // Log for debugging purposes only
      console.error('API Error in check-valid-car:', {
        status: error.response?.status,
        message: error.response?.data?.message,
        url: error.config?.url
      });
    }
  };

  // Handle final confirmation to add the car
  const handleConfirmAdd = async () => {
    // Final validation before sending to API
    if (!isOwnershipVerification) {
      const hasValidVin = newCar.vin && vinValidated;
      // VIN is required for regular flow
      if (!hasValidVin) {
        toast.error(t('vehicleDetails.errors.vinRequired'));
        return;
      }
    }

    // If owner is true, CIV must be valid
    if (newCar.owner) {
      const hasValidCiv = newCar.civ && civValidated;
      if (!hasValidCiv) {
        toast.error(t('firstLogin.civRequiredForOwner') || 'CIV este obligatoriu pentru proprietari');
        return;
      }
    }

    try {
      const idToken = await auth.currentUser?.getIdToken();

      if (isOwnershipVerification) {
        // Use ownership verification endpoint
        await axios.post(
          'https://api.servicegest.ro/driver/car/verify-ownership',
          null,
          {
            params: {
              carId: carId,
              civ: newCar.civ
            },
            headers: {
              'Authorization': `Bearer ${idToken}`,
              'Content-Type': 'application/json',
            }
          }
        );
      } else {
        // Use regular add car endpoint
        await axios.post(
          'https://api.servicegest.ro/driver/car/add',
          {
            owner: newCar.owner,
            registrationNumber: newCar.plate,
            vin: newCar.vin, // VIN is required for regular flow
            civ: newCar.civ || undefined
          },
          {
            headers: {
              'Authorization': `Bearer ${idToken}`,
              'Content-Type': 'application/json',
            }
          }
        );
      }

      // Refresh parent car list and close modals
      if (onCarsChanged) {
        onCarsChanged(); // Refresh parent car list
      }
      setShowAddCarModal(false);
      setShowConfirmationModal(false);

      if (isOwnershipVerification) {
        toast.success(t('vehicleDetails.ownershipVerified') || 'Proprietatea a fost verificată cu succes!');
      } else {
        toast.success(t('addVehicle.addSuccess'));
      }
    } catch (error) {
      if (isOwnershipVerification) {
        toast.error(t('vehicleDetails.ownershipVerificationError') || 'Eroare la verificarea proprietății!');
      } else {
        toast.error(t('addVehicle.addError'));
      }

      // Log for debugging purposes only
      console.error('API Error in car add:', {
        status: error.response?.status,
        message: error.response?.data?.message,
        url: error.config?.url
      });
    }
  };

  // Handle cancellation
  const handleCancelAdd = () => {
    setShowConfirmationModal(false);
    setCarDetails(null);
  };

  // Handle escape key and click outside to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (showCivHelpModal) {
          setShowCivHelpModal(false);
        } else if (showConfirmationModal) {
          handleCancelAdd();
        } else {
          setShowAddCarModal(false);
        }
      }
    };

    const handleClickOutside = (e) => {
      // Check if the click is outside all modals
      const mainModal = modalRef.current;
      const confirmationModal = document.querySelector('[data-confirmation-modal]');
      const civHelpModal = document.querySelector('[data-civ-help-modal]');

      const isInsideMainModal = mainModal && mainModal.contains(e.target);
      const isInsideConfirmationModal = confirmationModal && confirmationModal.contains(e.target);
      const isInsideCivHelpModal = civHelpModal && civHelpModal.contains(e.target);

      // Only close if click is outside all modals
      if (!isInsideMainModal && !isInsideConfirmationModal && !isInsideCivHelpModal) {
        if (showCivHelpModal) {
          setShowCivHelpModal(false);
        } else if (showConfirmationModal) {
          handleCancelAdd();
        } else {
          setShowAddCarModal(false);
        }
      }
    };

    if (showAddCarModal || showConfirmationModal || showCivHelpModal) {
      document.addEventListener('keydown', handleEscape);
      // Use mousedown to catch clicks that start outside the modal
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAddCarModal, showConfirmationModal, showCivHelpModal, setShowAddCarModal]);

  if (!showAddCarModal) return null;

  // Helper function for input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Handle checkbox inputs differently
    const inputValue = type === 'checkbox' ? checked : value;
    setNewCar(prev => ({ ...prev, [name]: inputValue }));
  };

  // Set owner flag and clear CIV & its validation when switching to "not owner"
  const handleSetOwner = (value) => {
    setNewCar(prev => ({ ...prev, owner: value, // clear civ when not owner
      civ: value ? prev.civ : ''
    }));

    if (!value) {
      // clear CIV validation states when user selects "Not owner"
      setCivValidated(false);
      setCivValidating(false);
      setCivError(false);
    }
  };

  return (
    <div className="fixed inset-0 z-20 bg-slate-100/10 dark:bg-slate-900/10 backdrop-blur-sm flex justify-center items-center p-2">
      <div
        ref={modalRef}
        className="relative w-full max-w-md mb-8 max-h-[calc(90vh-4rem)] flex flex-col rounded-xl bg-white dark:bg-slate-800 shadow-2xl border dark:border-slate-700"
      >
        {/* Header */}
        <div className="px-2 py-2 border-b border-slate-200 dark:border-slate-700 rounded-t-xl bg-purple-50 dark:bg-purple-900/20 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <TruckIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-slate-900 dark:text-white truncate">
                  {prefilledPlate ? t('vehicleDetails.takeOwnershipTitle') : t('addVehicle.title')}
                </h3>
                {/* <p className="text-sm text-slate-600 dark:text-gray-400">
                  {prefilledPlate ? t('vehicleDetails.takeOwnershipSubtitle') : t('vehicleDetails.subtitle')}
                </p> */}
              </div>
            </div>
            <button
              onClick={() => setShowAddCarModal(false)}
              className="p-2 text-gray-400 hover:text-slate-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className=" overflow-y-auto">
          <div className="bg-white dark:bg-slate-800">

            <form className="p-2 space-y-2" onSubmit={handleSubmit} id="add-vehicle-form">
              {/* Checkbox Field: Sunt proprietarul acestei masini */}
              <div className="flex items-center justify-center">
                {/* Segmented control for owner / not owner */}
                <div className="relative flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1 w-64 h-12">
                  <div
                    className={`absolute top-1 left-1 w-1/2 h-10 bg-purple-600 rounded-md transition-transform duration-300 ${newCar.owner ? 'translate-x-0' : 'translate-x-full'}`}
                    aria-hidden
                  />

                  <button
                    type="button"
                    onClick={() => !prefilledPlate && handleSetOwner(true)}
                    disabled={!!prefilledPlate}
                    className={`flex-1 flex items-center justify-center z-10 text-sm font-medium transition-colors ${newCar.owner ? 'text-white' : 'text-gray-600 dark:text-gray-400'}`}
                  >
                    <UserIcon className="w-5 h-5 mr-2" />
                    Proprietar
                  </button>

                  <button
                    type="button"
                    onClick={() => !prefilledPlate && handleSetOwner(false)}
                    disabled={!!prefilledPlate}
                    className={`flex-1 flex items-center justify-center z-10 text-sm font-medium transition-colors ${!newCar.owner ? 'text-white' : 'text-gray-600 dark:text-gray-400'}`}
                  >
                    <BriefcaseIcon className="w-5 h-5 mr-2" />
                    Nu
                  </button>
                </div>

                {prefilledPlate && (
                  <span className="ml-2 text-xs text-blue-600 dark:text-blue-400 font-medium">
                    ({t('vehicleDetails.takingOwnership')})
                  </span>
                )}
              </div>

              {/* Input Field 3: Serie șasiu - Only show if not ownership verification */}
              {!isOwnershipVerification && (
                <div className="relative">
                  <label htmlFor="vin" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    {t('addVehicle.vin')} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="vin"
                      name="vin"
                      placeholder={`${t('addVehicle.vinPlaceholder')} (obligatoriu)`}
                      value={newCar.vin}
                      onChange={handleInputChange}
                      maxLength={17}
                      required
                      className={`w-full px-3 py-2 pr-10 border ${vinValidated ? 'border-purple-500' : vinError ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white sm:text-sm`}
                    />
                    {vinValidated && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <CheckCircleIcon className="h-5 w-5 text-purple-500" />
                      </div>
                    )}
                    {vinError && !vinValidating && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <XCircleIcon className="h-5 w-5 text-red-500" />
                      </div>
                    )}
                    {vinValidating && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <div className="animate-spin h-4 w-4 border-2 border-purple-500 border-t-transparent rounded-full"></div>
                      </div>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t('vehicleDetails.vinRequired')}
                  </p>
                </div>
              )}

              {/* Input Field: CIV - show only when user selected "Proprietar" */}
              {newCar.owner && (
                <div className="relative">
                  <label htmlFor="civ" className={`block text-sm font-medium mb-1 ${newCar.owner && !civValidated ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-slate-300'}`}>
                    CIV {newCar.owner && <span className="text-red-500">*</span>}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="civ"
                      name="civ"
                      placeholder="R012345"
                      value={newCar.civ}
                      onChange={handleInputChange}
                      maxLength={7}
                      className={`w-full px-3 py-2 pr-10 border ${civValidated
                        ? 'border-purple-500'
                        : (newCar.owner && !civValidated && newCar.civ) || civError
                          ? 'border-red-500'
                          : newCar.owner && !newCar.civ
                            ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                            : 'border-gray-300 dark:border-gray-600'
                        } rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white sm:text-sm`}
                    />
                    {civValidated && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <CheckCircleIcon className="h-5 w-5 text-purple-500" />
                      </div>
                    )}
                    {((newCar.owner && !civValidated && newCar.civ) || civError) && !civValidating && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <XCircleIcon className="h-5 w-5 text-red-500" />
                      </div>
                    )}
                    {civValidating && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <div className="animate-spin h-4 w-4 border-2 border-purple-500 border-t-transparent rounded-full"></div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <p className={`text-xs ${newCar.owner && !civValidated ? 'text-red-500 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
                      {newCar.owner && !civValidated
                        ? t('firstLogin.civRequiredForOwner') || 'CIV este obligatoriu pentru proprietari'
                        : t('vehicleDetails.civFormat')
                      }
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowCivHelpModal(true)}
                      className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 underline font-medium"
                    >
                      Unde pot găsi CIV-ul?
                    </button>
                  </div>
                </div>
              )}

              {/* Input Field 4: Nr. înmatriculare */}
              <div>
                <label htmlFor="plate" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">{t('addVehicle.plate')}</label>
                <input
                  type="text"
                  id="plate"
                  name="plate"
                  placeholder={t('addVehicle.platePlaceholder')}
                  value={newCar.plate}
                  onChange={handleInputChange}
                  required
                  disabled={!!prefilledPlate}
                  className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white sm:text-sm ${prefilledPlate ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`}
                />
                {prefilledPlate && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t('vehicleDetails.platePrefilledNote')}
                  </p>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 flex justify-end space-x-3 rounded-b-xl flex-shrink-0">
          <button
            type="button"
            onClick={() => setShowAddCarModal(false)}
            className="bg-white dark:bg-gray-700 text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600 font-medium py-2 px-4 rounded-lg transition-colors"
          >
            {t('common.close')}
          </button>
          <button
            type="submit"
            form="add-vehicle-form"
            disabled={
              !newCar.plate ||
              (!isOwnershipVerification && (!vinValidated || vinValidating)) ||
              civValidating ||
              (newCar.owner && !civValidated)
            }
            className={`inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white ${!newCar.plate ||
              (!isOwnershipVerification && (!vinValidated || vinValidating)) ||
              civValidating ||
              (newCar.owner && !civValidated)
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 dark:focus:ring-offset-gray-800'
              }`}
          >
            {(!isOwnershipVerification && vinValidating) || civValidating ?
              t('vehicleDetails.verifying') :
              isOwnershipVerification ?
                t('vehicleDetails.confirmOwnership') || 'Confirmă Proprietatea' :
                t('vehicleDetails.reviewDetails')
            }
          </button>
        </div>
      </div>

      {/* Car Details Confirmation Modal */}
      {showConfirmationModal && carDetails && (
        <div className="fixed inset-0 z-30 bg-slate-100/10 dark:bg-slate-900/10 backdrop-blur-sm flex justify-center items-center p-2">
          <div className="relative w-full max-w-2xl mb-8 max-h-[calc(90vh-4rem)] flex flex-col rounded-xl bg-white dark:bg-slate-800 shadow-2xl border dark:border-slate-700" data-confirmation-modal>
            {/* Header */}
            <div className="px-2 py-2 border-b border-slate-200 dark:border-slate-700 rounded-t-xl bg-purple-50 dark:bg-purple-900/20 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <CheckCircleIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                <h3 className="text-lg sm:text-xl font-semibold text-slate-900 dark:text-white truncate">
                      {isOwnershipVerification ?
                        (t('vehicleDetails.confirmOwnership') || 'Confirmă Proprietatea') :
                        t('vehicleDetails.confirmDetails')
                      }
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-gray-400">
                      {isOwnershipVerification ?
                        (t('vehicleDetails.reviewOwnership') || 'Verifică și confirmă proprietatea vehiculului') :
                        t('vehicleDetails.reviewConfirm')
                      }
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCancelAdd}
                  className="p-2 text-gray-400 hover:text-slate-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="overflow-y-auto flex-1 p-6">
              <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-slate-900 dark:text-white border-b border-slate-200 dark:border-slate-700 pb-2">
                    {t('vehicleDetails.basicInfo')}
                  </h3>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">{t('vehicleDetails.make')}</label>
                      <p className="mt-1 text-sm text-slate-900 dark:text-white bg-gray-50 dark:bg-slate-700 p-2 rounded">
                        {carDetails.make || 'N/A'}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">{t('vehicleDetails.model')}</label>
                      <p className="mt-1 text-sm text-slate-900 dark:text-white bg-gray-50 dark:bg-slate-700 p-2 rounded">
                        {carDetails.type || 'N/A'}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">{t('vehicleDetails.year')}</label>
                      <p className="mt-1 text-sm text-slate-900 dark:text-white bg-gray-50 dark:bg-slate-700 p-2 rounded">
                        {carDetails.year || 'N/A'}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">{t('vehicleDetails.color')}</label>
                      <p className="mt-1 text-sm text-slate-900 dark:text-white bg-gray-50 dark:bg-slate-700 p-2 rounded">
                        {carDetails.color || 'N/A'}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">{t('vehicleDetails.bodyType')}</label>
                      <p className="mt-1 text-sm text-slate-900 dark:text-white bg-gray-50 dark:bg-slate-700 p-2 rounded">
                        {carDetails.bodyType || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Technical Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-slate-900 dark:text-white border-b border-slate-200 dark:border-slate-700 pb-2">
                    {t('vehicleDetails.technicalInfo')}
                  </h3>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">VIN</label>
                      <p className="mt-1 text-sm text-slate-900 dark:text-white bg-gray-50 dark:bg-slate-700 p-2 rounded font-mono">
                        {carDetails.vin || 'N/A'}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">{t('vehicleDetails.registrationNumber')}</label>
                      <p className="mt-1 text-sm text-slate-900 dark:text-white bg-gray-50 dark:bg-slate-700 p-2 rounded font-mono">
                        {carDetails.registrationNr || 'N/A'}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">{t('vehicleDetails.engineCapacity')}</label>
                      <p className="mt-1 text-sm text-slate-900 dark:text-white bg-gray-50 dark:bg-slate-700 p-2 rounded">
                        {carDetails.engineCapacity ? `${carDetails.engineCapacity}L` : 'N/A'}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">{t('vehicleDetails.engineNumber')}</label>
                      <p className="mt-1 text-sm text-slate-900 dark:text-white bg-gray-50 dark:bg-slate-700 p-2 rounded font-mono">
                        {carDetails.engineNumber || 'N/A'}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">{t('vehicleDetails.engineId')}</label>
                      <p className="mt-1 text-sm text-slate-900 dark:text-white bg-gray-50 dark:bg-slate-700 p-2 rounded font-mono">
                        {carDetails.engineId || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Additional Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-slate-900 dark:text-white border-b border-slate-200 dark:border-slate-700 pb-2">
                    {t('vehicleDetails.additionalInfo')}
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">{t('vehicleDetails.mileage')}</label>
                      <p className="mt-1 text-sm text-slate-900 dark:text-white bg-gray-50 dark:bg-slate-700 p-2 rounded">
                        {carDetails.km ? `${carDetails.km} km` : 'N/A'}
                      </p>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">{t('vehicleDetails.inspectionDue')}</label>
                      <p className="mt-1 text-sm text-slate-900 dark:text-white bg-gray-50 dark:bg-slate-700 p-2 rounded">
                        {carDetails.inspectionDue ? new Date(carDetails.inspectionDue).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-3 bg-gray-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 flex justify-end space-x-3 rounded-b-xl flex-shrink-0">
              <button
                type="button"
                onClick={handleCancelAdd}
                    className="flex-1 py-2 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 border border-slate-300 dark:border-slate-600 font-medium rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleConfirmAdd}
                    className="flex-1 py-2 rounded-lg font-semibold text-white bg-purple-600 hover:bg-purple-700 shadow-lg transition-colors"
              >
                {isOwnershipVerification ?
                  (t('vehicleDetails.confirmOwnership') || 'Confirmă Proprietatea') :
                  t('vehicleDetails.confirmAdd')
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CIV Help Modal */}
      {showCivHelpModal && (
        <div className="fixed inset-0 z-40 bg-slate-100/10 dark:bg-slate-900/10 backdrop-blur-sm flex justify-center items-center p-2">
          <div className="relative w-full max-w-4xl mb-8 max-h-[calc(80vh-4rem)] flex flex-col rounded-xl bg-white dark:bg-slate-800 shadow-2xl border dark:border-slate-700" data-civ-help-modal>
            {/* Header */}
            <div className="px-2 py-2 border-b border-slate-200 dark:border-slate-700 rounded-t-xl bg-purple-50 dark:bg-purple-900/20 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <InformationCircleIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                <h3 className="text-lg sm:text-xl font-semibold text-slate-900 dark:text-white truncate">
                      Unde pot găsi CIV-ul?
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-gray-400">
                      CIV-ul se găsește în două locuri principale
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowCivHelpModal(false)}
                  className="p-2 text-gray-400 hover:text-slate-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="overflow-y-auto flex-1 p-2">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Cartea mașinii */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-slate-900 dark:text-white border-b border-slate-200 dark:border-slate-700 pb-2">
                    Pe cartea mașinii
                  </h3>
                  <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-4">
                    <img
                      src="https://storage.servicegest.ro/public/utils/seria-civ-pe-cartea-masinii.jpg"
                      alt="CIV pe cartea mașinii"
                      className="w-full h-auto rounded-lg shadow-md"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div className="hidden text-center text-gray-500 dark:text-gray-400 py-8">
                      Imaginea nu poate fi încărcată
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    CIV-ul se găsește pe prima pagină a cărții de identitate a vehiculului, în partea de sus.
                  </p>
                </div>

                {/* Talonul */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-slate-900 dark:text-white border-b border-slate-200 dark:border-slate-700 pb-2">
                    Pe talon
                  </h3>
                  <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-4">
                    <img
                      src="https://storage.servicegest.ro/public/utils/seria-civ-pe-talon.jpg"
                      alt="CIV pe talon"
                      className="w-full h-auto rounded-lg shadow-md"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div className="hidden text-center text-gray-500 dark:text-gray-400 py-8">
                      Imaginea nu poate fi încărcată
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    CIV-ul se găsește și pe talon, în partea de sus a documentului.
                  </p>
                </div>
              </div>

              {/* Additional info */}
              <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                  Informații importante:
                </h4>
                <ul className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
                  <li>• CIV-ul este format dintr-o literă urmată de 6 cifre (ex: R012345)</li>
                  <li>• Este același pe ambele documente (cartea mașinii și talon)</li>
                  <li>• Este necesar doar dacă sunteți proprietarul vehiculului</li>
                </ul>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-3 bg-gray-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 flex justify-end rounded-b-xl flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowCivHelpModal(false)}
                className="bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Am înțeles
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddVehicleModal;
